import { z } from "zod";

/**
 * A PDF statement layout, as data.
 *
 * The engine that reads a PDF is hand-written and identical for every bank;
 * everything bank-specific lives here, in a document a person can read and
 * approve. Never generated code — a profile that is wrong produces a refused
 * import, not an unreviewable extractor that fails silently when a bank moves
 * a column four points.
 *
 * Extraction must be deterministic given (PDF bytes + profile), because commit
 * re-reads the archived file from R2 and re-runs the whole pipeline rather than
 * trusting client-sent rows (`app/src/import/statements.ts`).
 */

/**
 * `debit`/`credit` are unsigned magnitudes in two columns; `amount` is a single
 * signed column. `ignore` lets a profile name a column it must skip over, so
 * the band arithmetic still sees the right number of columns.
 */
export const PdfColumnRole = z.enum([
  "date",
  "description",
  "debit",
  "credit",
  "amount",
  "balance",
  "ignore",
]);

/** A regex supplied as configuration. Rejected here rather than throwing mid-import. */
const Pattern = z.string().max(200).refine(compiles, "That is not a valid regular expression");

function compiles(source: string): boolean {
  try {
    new RegExp(source);
    return true;
  } catch {
    return false;
  }
}

export const StatementProfile = z.strictObject({
  version: z.literal(1),
  label: z.string().trim().min(1).max(80),

  header: z.strictObject({
    /**
     * The page gate: a page carries a table iff every one of these appears, in
     * order, on one line. Without it a fees summary full of money-like numbers
     * (`£18 per month`, `39.9%`) corrupts every total.
     *
     * Matched whitespace-stripped and lowercased, so `£ Out`, `£Out` and
     * `£  Out` are the same label.
     */
    labels: z.array(z.string().trim().min(1)).min(2).max(10),
    lineTolerance: z.number().min(0).max(6).default(2),
  }),

  /**
   * One entry per column, `header` naming a member of `header.labels`.
   *
   * Anchors are re-read from every page rather than stored: the same statement
   * shifts its columns 3.1–3.4pt between page 1 and page 2, so absolute bands
   * would be wrong by page 2.
   *
   * `align` is not cosmetic. Nationwide and Monzo right-align their money;
   * Tide left-aligns all three of its money columns, so a right-edge-only
   * engine misfiles Tide entirely.
   */
  columns: z
    .array(
      z.strictObject({
        role: PdfColumnRole,
        header: z.string().trim().min(1),
        align: z.enum(["left", "right"]),
      }),
    )
    .min(2)
    .max(10),

  body: z.strictObject({
    /** How far below the header line the body starts. */
    topGap: z.number().min(0).max(20).default(2),
    /** Everything at or below this y is page furniture. Belt; `drop` is braces. */
    bottomY: z.number().min(0).max(200).default(0),
    /** Slack to the left of the first anchor, for a column that prints left of its header. */
    leftSlack: z.number().min(0).max(30).default(6),
    /**
     * How far the last column extends past its own anchor. Its job is excluding
     * the sidebar: a `£1,025.30` printed at x 529 becomes a phantom checkpoint
     * if the last band is open-ended on the right.
     */
    rightExtent: z.number().min(1).max(300),
    /**
     * Merge two runs into one word when the gap between them is under this.
     * Monzo renders pence at a smaller size than pounds, so `£274` and `.74`
     * arrive as separate runs 0.01pt apart and must be joined.
     */
    wordGap: z.number().min(0).max(6).default(1.5),
    rowTolerance: z.number().min(0.5).max(8).default(2),
    columnSlack: z.number().min(0).max(10).default(3),
  }),

  /**
   * Where the statement stops being about this account.
   *
   * A Monzo statement is not one account: the Personal Account comes first,
   * then a Pot statement per Pot, and the Pot tables reuse a byte-identical
   * header. The header page gate admits all of them and merges three ledgers
   * into one. The reconciler refuses that rather than importing it, but refusing
   * is not importing.
   *
   * TODO: revisit. This assumes the account we want comes first and that a
   * single phrase marks the break, which is true of the Monzo statements
   * measured but is a weaker rule than "a table counts only on a page that
   * says which account it belongs to". Worth reconsidering once the AI
   * derivation has seen more layouts.
   */
  section: z
    .strictObject({
      /** Stop reading at the first page containing text matching this. */
      stopAfterPattern: Pattern.nullable().default(null),
    })
    .prefault({}),

  /** A newest-first statement reconciles backwards. Cheap to declare, unreliable to detect. */
  order: z.enum(["oldest-first", "newest-first"]).default("oldest-first"),

  dates: z.strictObject({
    /**
     * `in-date` — the cell carries its own year (`26/07/2026`, `24 Jul 2026`).
     * `column-marker` — a bare year is printed above the date column, which
     * handles a Dec→Jan rollover natively.
     * `statement-date` — derive from the statement date, decrementing the year
     * when the month runs ahead of it.
     */
    yearSource: z.enum(["in-date", "column-marker", "statement-date"]).default("in-date"),
    yearMarkerPattern: Pattern.default("^(\\d{4})$"),
    statementDateLabel: z.string().max(80).default("Statement date"),
    carryForward: z.boolean().default(true),
    /**
     * The guard the balance chain cannot provide: `start + Σ = end` holds
     * whatever year you stamp on a row, so a Dec→Jan bug is invisible to it.
     */
    windowDays: z.number().int().min(1).max(400).default(70),
  }).prefault({}),

  continuation: z.strictObject({
    joiner: z.string().max(8).default(" / "),
    maxLines: z.number().int().min(0).max(20).default(6),
    /**
     * Where a description-only line belongs.
     *
     * `following` — it continues the row above it, which is how Nationwide and
     * Tide wrap. `nearest` — it belongs to whichever money-bearing line is
     * closest, which is how Monzo wraps: it centres the date, amount and
     * balance against the description block, so a wrapped line can sit *above*
     * its own row's baseline. Getting this wrong glues text to the neighbouring
     * transaction, and the reconciler cannot see it.
     */
    attach: z.enum(["following", "nearest"]).default("following"),
  }).prefault({}),

  /** Page furniture: removed from everything. */
  drop: z.strictObject({ rowPatterns: z.array(Pattern).max(20).default([]) }).prefault({}),

  /**
   * Kept **in the balance chain** but out of the ledger — a brought-forward
   * balance is the case in hand. This is what lets the reconciler stay exact
   * while the ledger stays clean.
   */
  ledgerExclude: z.strictObject({ rowPatterns: z.array(Pattern).max(20).default([]) }).prefault({}),

  reconcile: z.strictObject({
    /** `none` for a bank that prints no running balance: import, but unchecked. */
    balanceRole: z.enum(["balance", "none"]).default("balance"),
    /**
     * Patterns, not literal strings: Tide prints `Balance (£) on 1 Jul 2026`,
     * so the label carries a date that changes every month.
     *
     * In all three banks measured these sit *outside* the table bands, so they
     * are found by scanning the document for a line matching the pattern and
     * taking the rightmost money-shaped word on it.
     */
    startLabel: Pattern.nullable().default(null),
    endLabel: Pattern.nullable().default(null),
    /**
     * Deliberately not a number. The moment it is, someone sets it to 1 and the
     * checksum stops being a checksum.
     */
    tolerancePence: z.literal(0).default(0),
  }).prefault({}),
});

export type PdfColumnRole = z.infer<typeof PdfColumnRole>;
export type StatementProfile = z.infer<typeof StatementProfile>;
